<template>
  <div>
    <form @submit.stop.prevent="form.handleSubmit()">
      <form.Field
        name="label"
        :validators="{
          onBlur: yup.string().required('Label is required')
        }"
      >
        <template #default="{ field }">
          <BaseInput
            v-model="model.label"
            :name="field.name"
            label="Label"
            placeholder="Label"
            :disabled="isUpdating"
            :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
            @blur="field.handleBlur"
            @input="(e) => field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <form.Field name="partial">
        <template #default="{ field }">
          <div class="mb-4">
            <span class="select-none text-base/6 text-slate-950 sm:text-sm/6 font-medium inline-block mb-2">Is Template Partial?</span>

            <BaseSwitch
              v-model="model.partial"
              :name="field.name"
              @update:model-value="(value) => field.handleChange(value)"
            />
          </div>
        </template>
      </form.Field>

      <form.Field name="subject">
        <template #default="{ field }">
          <BaseInput
            v-model="model.subject"
            :name="field.name"
            label="Subject"
            placeholder="Email Subject"
            @input="(e) => field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <div class="mt-4">
        <form.Field name="html">
          <template #default="{ field }">
            <CodeEditor
              v-model="model.html"
              :name="field.name"
              lang="html"
              label="HTML"
              @update:model-value="(value) => field.handleChange(value)"
            />
          </template>
        </form.Field>
      </div>

      <div class="mt-4">
        <form.Field name="text">
          <template #default="{ field }">
            <BaseTextArea
              v-model="model.text"
              :name="field.name"
              label="Plaintext"
              placeholder="Email Plaintext"
              rows="8"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>
      </div>

      <div class="flex justify-end gap-2 mt-3">
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <BaseButton
              text="Preview"
              type="button"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
              @click="showModal = true"
            />

            <BaseButton
              variant="primary"
              type="submit"
              :disabled="!canSubmit || isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
            >
              <div class="flex justify-between items-center gap-2">
                <span>Save</span>
                <span
                  v-if="isSubmitting"
                  class="i-heroicons-arrow-path animate-spin"
                />
              </div>
            </BaseButton>
          </template>
        </form.Subscribe>
      </div>
    </form>
  </div>

  <AppModal
    v-model="showModal"
    :title="`${model.name}-preview-modal`"
    width="md:w-[46rem] w-full"
  >
    <PreviewEmailTemplate
      :template="model"
      @close="showModal = false"
    />
  </AppModal>
</template>

<script lang="ts" setup>
import BaseInput from '@/components/base/BaseInput.vue'
import BaseTextArea from '@/components/base/BaseTextArea.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { snakeCase } from '@/utils'
import { extend } from 'underscore'
import PreviewEmailTemplate from './PreviewEmailTemplate.vue'
import CodeEditor from '@/components/CodeEditor.vue'
import EmailService, { EmailTemplate } from '@/services/EmailService'
import { PropType, ref } from 'vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import BaseSwitch from '@/components/base/BaseSwitch.vue'
import { useMutation } from '@tanstack/vue-query'
import { useNotification } from '@kyvg/vue3-notification'
import AppModal from '@/components/app/AppModal.vue'

const props = defineProps({
  template: {
    type: Object as PropType<EmailTemplate>,
    required: false,
    default: () => ({
      ...new EmailTemplate(),
      html: '{{#> base_layout }}{{#*inline "content"}}\n<--- content here --->\n{{/inline}}{{/base_layout}}',
    })
  },
  isUpdating: {
    type: Boolean,
    default: true,
  }
})

const model = ref<EmailTemplate>(extend({}, props.template))
const showModal = ref(false)

const emailService = new EmailService()
const { notify } = useNotification()
const emit = defineEmits(['update'])

const form = useForm({
  defaultValues: model.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    await saveTemplate(value)
  }
})

const { mutateAsync: saveTemplate } = useMutation({
  mutationFn: async (template: EmailTemplate) =>  emailService.update(snakeCase(template.label) ,template),
  onSuccess: (updatedTemplate: EmailTemplate) => {
    emit('update', updatedTemplate)
    notify({
      title: 'Success',
      text: 'Email template saved!',
      type: 'success'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while trying to save the email template.'
    })
  }
})

</script>
